import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import type {
  GenerationJob,
  Page,
  SeedEvent,
  SeedEventStatus,
  SeedEventType,
  Source,
} from "@/types";

/**
 * React Query hooks for the admin Seed Events feature. These wrap the existing
 * API client methods — the client is unchanged — and own the query keys,
 * caching, invalidation-on-success, and generation-job polling (via
 * `refetchInterval`) that previously lived as manual `useEffect` loaders and the
 * `pollGenerationJob` helper. Follows the Sources / Assessment Fixtures slice
 * templates.
 */

export interface SeedEventsListParams {
  page: number;
  page_size: number;
  status?: SeedEventStatus;
  event_type?: SeedEventType;
  q?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
}

export const seedEventKeys = {
  all: ["admin", "seedEvents"] as const,
  lists: () => [...seedEventKeys.all, "list"] as const,
  list: (params: SeedEventsListParams) => [...seedEventKeys.lists(), params] as const,
  sources: () => [...seedEventKeys.all, "sources"] as const,
  job: (jobId: string) => [...seedEventKeys.all, "job", jobId] as const,
};

export function useSeedEvents(params: SeedEventsListParams) {
  return useQuery<Page<SeedEvent>>({
    queryKey: seedEventKeys.list(params),
    queryFn: () => api.getSeedEvents(params),
  });
}

/** Active sources for the create/edit form's registry picker. */
export function useSeedEventSources() {
  return useQuery<Source[]>({
    queryKey: seedEventKeys.sources(),
    queryFn: async () => {
      const data = await api.getSources({ active_only: true });
      return data.items;
    },
  });
}

/**
 * Generation-job query. Polls every 2s until the job reaches a terminal status
 * (`completed`/`failed`), then stops. `enabled` gates the query so it only runs
 * once a job has been started.
 */
export function useGenerationJob(jobId: string | undefined, options: { poll?: boolean } = {}) {
  const { poll = false } = options;
  return useQuery<GenerationJob>({
    queryKey: seedEventKeys.job(jobId ?? ""),
    queryFn: () => api.getGenerationJob(jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: poll
      ? (query) => {
          const s = query.state.data?.status;
          return s === "completed" || s === "failed" ? false : 2000;
        }
      : undefined,
  });
}

/** Invalidate every Seed Events list query after a write. */
function useInvalidateSeedEvents() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: seedEventKeys.lists() });
}

export function useCreateSeedEvent() {
  const invalidate = useInvalidateSeedEvents();
  return useMutation({
    mutationFn: (data: Partial<SeedEvent>) => api.createSeedEvent(data),
    onSuccess: invalidate,
  });
}

export function useUpdateSeedEvent() {
  const invalidate = useInvalidateSeedEvents();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SeedEvent> }) =>
      api.updateSeedEvent(id, data),
    onSuccess: invalidate,
  });
}

export function useDeleteSeedEvent() {
  const invalidate = useInvalidateSeedEvents();
  return useMutation({
    mutationFn: (id: string) => api.deleteSeedEvent(id),
    onSuccess: invalidate,
  });
}

export function useGenerateFromSeedEvent() {
  return useMutation<GenerationJob, Error, { eventId: string; count: number }>({
    mutationFn: ({ eventId, count }) => api.generateFromSeedEvent(eventId, count),
  });
}
